/**
 * What the AI a pilot uses costs, from the providers' own public price lists —
 * an estimate, dated and sourced, and never a bill.
 *
 * ## The rules this table keeps
 *
 * - **Every price was read on the provider's own pricing page** on the date in
 *   `verifiedOn`, from the URL in `source`. Nothing is from memory and nothing
 *   is from a third-party aggregator. A model that is not here is `null` —
 *   rendered "unknown" — never priced like a model that looks similar.
 * - **The provider's bill is the authority.** Keys are the pilot's own; tiers,
 *   regional uplifts, batch discounts, credits and taxes are between the pilot
 *   and the provider and none of them is visible to us.
 * - **A quantity with no price makes the line unknown**, not cheaper: a TTS
 *   line that reports characters to a model priced only per token cannot be
 *   estimated, and saying $0 would be the reassuring wrong value rule 2 is
 *   about.
 * - **Local models are free**: `ollama` runs on the pilot's own machine and is
 *   priced 0 for any model, so the pilot sees it as such.
 *
 * ## What is deliberately simplified (and said so on screen)
 *
 * - Short-context rates only. OpenAI's GPT-6/5.6/5.5/5.4 change price above
 *   272K prompt tokens, xAI's Grok 4.x above 200K and Claude Haiku 5.5 above
 *   100K; a counter adds a day's
 *   requests together and cannot tell. The EFB assistant's and RogerATC's
 *   rounds are a few thousand tokens.
 * - Anthropic cache *writes* (1.25× input) are not separated; the assistant
 *   does not ask for prompt caching, so it pays none.
 * - OpenRouter passes providers' prices through without a markup (its FAQ),
 *   and charges 5.5 % when credits are bought — that fee is not included.
 *
 * Pure. Updating a price means a new period (or a new entry) with its own
 * `verifiedOn` and `source`, never editing the old figure in place: a cost
 * shown last month was computed from what was published last month.
 */

import type { AiUsageKind, AiUsageLine } from '@/core/ai/usage'

/** The day this table was last checked against every source below. */
export const PRICES_VERIFIED_ON = '2026-10-09'

/** USD. Every field is optional: a kind uses the ones that apply to it. */
export interface PriceRates {
	/** Per million input tokens (uncached). */
	inputPerMTok?: number
	/** Per million input tokens served from the provider's prompt cache. */
	cachedInputPerMTok?: number
	/** Per million output tokens (for `gpt-4o-mini-tts`, audio tokens). */
	outputPerMTok?: number
	/** Per minute of audio (STT input, or TTS output where billed by time). */
	perMinute?: number
	/** Per million characters of TTS input. */
	perMCharacters?: number
	/** Per request (a search). */
	perRequest?: number
}

export interface PricePeriod extends PriceRates {
	/** First UTC day these rates apply, inclusive. Absent: since before this table. */
	from?: string
	/** Last UTC day, inclusive. Absent: until a newer period says otherwise. */
	to?: string
}

export interface PriceEntry {
	provider: string
	/** The exact model id; a dated snapshot of it (`<id>-2025-10-01`, `<id>-20251001`) matches too. */
	model: string
	kind: AiUsageKind
	periods: readonly PricePeriod[]
	/** The official page the figures were read from. */
	source: string
	verifiedOn: string
	note?: string
}

const OPENAI = 'https://developers.openai.com/api/docs/pricing'
const ANTHROPIC = 'https://platform.claude.com/docs/en/about-claude/pricing'
const XAI = 'https://docs.x.ai/docs/models'
const GOOGLE = 'https://ai.google.dev/gemini-api/docs/pricing'
const TAVILY = 'https://docs.tavily.com/documentation/api-credits'

const V = PRICES_VERIFIED_ON
/**
 * The entries added with the assistant's per-answer cost, read on the same
 * three official pages on this day. The entries above were re-read the same
 * day and had not moved; `PRICES_VERIFIED_ON` stays at the older date because
 * Google's and Tavily's were not re-read.
 */
const V12 = '2026-10-12'
/**
 * OpenAI's pricing page read again on 2026-10-14, the day it announced the
 * retirement of the GPT-4o and GPT-4.1 families: the priority tier ("Fast mode"
 * on that page) and `gpt-transcribe`, which RogerATC 1.9 beta uses by default.
 */
const V14 = '2026-10-14'

/** Retired by OpenAI, 2027-01-14 (announced 2026-10-14; replacement `gpt-6-luna`). */
const RETIRING_2027 = 'Retired by OpenAI on 2027-01-14 (announced 2026-10-14); replacement gpt-6-luna.'

/**
 * A model served at OpenAI's priority tier, as RogerATC meters it: the id with
 * `@priority`, written only when the response's own `service_tier` says the
 * request was served there (RogerATC 1.9 beta, `roger-llm/src/openai.rs`). The
 * plain id is the standard tier, so a pilot's assistant and RogerATC never share
 * a price that is not theirs.
 */
const priority = (
	model: string,
	rates: Pick<PriceRates, 'inputPerMTok' | 'cachedInputPerMTok' | 'outputPerMTok'>,
): PriceEntry =>
	llm(
		'openai',
		`${model}@priority`,
		OPENAI,
		rates,
		'Priority tier (OpenAI’s “Fast mode”), as RogerATC asks for it.',
		V14,
	)

const llm = (
	provider: string,
	model: string,
	source: string,
	rates: Pick<PriceRates, 'inputPerMTok' | 'cachedInputPerMTok' | 'outputPerMTok'>,
	note?: string,
	verifiedOn: string = V,
): PriceEntry => ({ provider, model, kind: 'llm', periods: [rates], source, verifiedOn, note })

const SHORT_OPENAI = 'Short context (≤ 272K input tokens).'

export const PRICE_TABLE: readonly PriceEntry[] = Object.freeze([
	// --- OpenAI: the EFB assistant's catalogue, and RogerATC's defaults ------
	llm(
		'openai',
		'gpt-6-astra',
		OPENAI,
		{ inputPerMTok: 10, cachedInputPerMTok: 1, outputPerMTok: 50 },
		'Short context (≤ 272K input tokens).',
	),
	llm(
		'openai',
		'gpt-5.6-terra',
		OPENAI,
		{ inputPerMTok: 2, cachedInputPerMTok: 0.2, outputPerMTok: 12 },
		'Short context (≤ 272K input tokens).',
	),
	llm(
		'openai',
		'gpt-5.6-luna',
		OPENAI,
		{ inputPerMTok: 0.2, cachedInputPerMTok: 0.02, outputPerMTok: 1.2 },
		'Short context (≤ 272K input tokens).',
	),
	llm(
		'openai',
		'gpt-4o-mini',
		OPENAI,
		{ inputPerMTok: 0.15, cachedInputPerMTok: 0.075, outputPerMTok: 0.6 },
		`RogerATC’s controller model until 1.9 beta. ${RETIRING_2027}`,
	),
	llm('openai', 'gpt-5-mini', OPENAI, { inputPerMTok: 0.25, cachedInputPerMTok: 0.025, outputPerMTok: 2 }),
	// The current catalogue and the models a pilot is likely to type, 2026-10-12.
	llm(
		'openai',
		'gpt-6.1-sol',
		OPENAI,
		{ inputPerMTok: 2, cachedInputPerMTok: 0.1, outputPerMTok: 10 },
		SHORT_OPENAI,
		V12,
	),
	llm(
		'openai',
		'gpt-6-sol',
		OPENAI,
		{ inputPerMTok: 2, cachedInputPerMTok: 0.2, outputPerMTok: 10 },
		SHORT_OPENAI,
		V12,
	),
	llm(
		'openai',
		'gpt-6-luna',
		OPENAI,
		{ inputPerMTok: 0.1, cachedInputPerMTok: 0.01, outputPerMTok: 0.5 },
		`${SHORT_OPENAI} RogerATC’s default model since 1.9 beta, at the priority tier.`,
		V12,
	),
	// RogerATC 1.9 beta asks for the priority tier: as fast as gpt-4o-mini was
	// (1,082 ms p50 against 1,046 ms, measured 2026-10-14) and, cached, cheaper.
	priority('gpt-6-luna', { inputPerMTok: 0.2, cachedInputPerMTok: 0.02, outputPerMTok: 1 }),
	priority('gpt-6-sol', { inputPerMTok: 4, cachedInputPerMTok: 0.4, outputPerMTok: 20 }),
	priority('gpt-6.1-sol', { inputPerMTok: 4, cachedInputPerMTok: 0.2, outputPerMTok: 20 }),
	priority('gpt-5.6-luna', { inputPerMTok: 0.4, cachedInputPerMTok: 0.04, outputPerMTok: 2.4 }),
	priority('gpt-5.4-mini', { inputPerMTok: 1.5, cachedInputPerMTok: 0.15, outputPerMTok: 9 }),
	priority('gpt-4o-mini', { inputPerMTok: 0.25, cachedInputPerMTok: 0.125, outputPerMTok: 1 }),
	llm(
		'openai',
		'gpt-5.6-sol',
		OPENAI,
		{ inputPerMTok: 4, cachedInputPerMTok: 0.4, outputPerMTok: 20 },
		SHORT_OPENAI,
		V12,
	),
	llm(
		'openai',
		'gpt-5.5',
		OPENAI,
		{ inputPerMTok: 5, cachedInputPerMTok: 0.5, outputPerMTok: 30 },
		SHORT_OPENAI,
		V12,
	),
	llm(
		'openai',
		'gpt-5.4',
		OPENAI,
		{ inputPerMTok: 2.5, cachedInputPerMTok: 0.25, outputPerMTok: 15 },
		SHORT_OPENAI,
		V12,
	),
	llm(
		'openai',
		'gpt-5.4-mini',
		OPENAI,
		{ inputPerMTok: 0.75, cachedInputPerMTok: 0.075, outputPerMTok: 4.5 },
		undefined,
		V12,
	),
	llm(
		'openai',
		'gpt-5.4-nano',
		OPENAI,
		{ inputPerMTok: 0.2, cachedInputPerMTok: 0.02, outputPerMTok: 1.25 },
		undefined,
		V12,
	),
	llm(
		'openai',
		'gpt-5.2',
		OPENAI,
		{ inputPerMTok: 1.75, cachedInputPerMTok: 0.175, outputPerMTok: 14 },
		undefined,
		V12,
	),
	llm(
		'openai',
		'gpt-5.1',
		OPENAI,
		{ inputPerMTok: 1.25, cachedInputPerMTok: 0.125, outputPerMTok: 10 },
		undefined,
		V12,
	),
	llm(
		'openai',
		'gpt-5',
		OPENAI,
		{ inputPerMTok: 1.25, cachedInputPerMTok: 0.125, outputPerMTok: 10 },
		undefined,
		V12,
	),
	llm(
		'openai',
		'gpt-5-nano',
		OPENAI,
		{ inputPerMTok: 0.05, cachedInputPerMTok: 0.005, outputPerMTok: 0.4 },
		undefined,
		V12,
	),
	llm(
		'openai',
		'gpt-4.1',
		OPENAI,
		{ inputPerMTok: 2, cachedInputPerMTok: 0.5, outputPerMTok: 8 },
		RETIRING_2027,
		V12,
	),
	llm(
		'openai',
		'gpt-4.1-mini',
		OPENAI,
		{ inputPerMTok: 0.4, cachedInputPerMTok: 0.1, outputPerMTok: 1.6 },
		RETIRING_2027,
		V12,
	),
	llm(
		'openai',
		'gpt-4o',
		OPENAI,
		{ inputPerMTok: 2.5, cachedInputPerMTok: 1.25, outputPerMTok: 10 },
		RETIRING_2027,
		V12,
	),
	llm(
		'openai',
		'o3',
		OPENAI,
		{ inputPerMTok: 2, cachedInputPerMTok: 0.5, outputPerMTok: 8 },
		undefined,
		V12,
	),
	llm(
		'openai',
		'o4-mini',
		OPENAI,
		{ inputPerMTok: 1.1, cachedInputPerMTok: 0.275, outputPerMTok: 4.4 },
		undefined,
		V12,
	),
	{
		provider: 'openai',
		model: 'gpt-4o-mini-transcribe',
		kind: 'stt',
		periods: [{ perMinute: 0.003 }],
		source: OPENAI,
		verifiedOn: V,
		note: 'RogerATC’s recogniser until 1.9 beta, and its fallback since. OpenAI’s own estimated cost per minute of audio. Retired by OpenAI on 2027-02-26 (announced 2026-10-14); replacement gpt-transcribe.',
	},
	{
		provider: 'openai',
		model: 'gpt-transcribe',
		kind: 'stt',
		periods: [{ perMinute: 0.0045 }],
		source: OPENAI,
		verifiedOn: V14,
		note: 'RogerATC’s default recogniser since 1.9 beta. Billed by the second of audio, reported in whole seconds.',
	},
	{
		provider: 'openai',
		model: 'gpt-4o-transcribe',
		kind: 'stt',
		periods: [{ perMinute: 0.006 }],
		source: OPENAI,
		verifiedOn: V,
		note: 'OpenAI’s own estimated cost per minute of audio.',
	},
	{
		provider: 'openai',
		model: 'whisper-1',
		kind: 'stt',
		periods: [{ perMinute: 0.006 }],
		source: OPENAI,
		verifiedOn: V,
	},
	{
		provider: 'openai',
		model: 'gpt-4o-mini-tts',
		kind: 'tts',
		periods: [{ inputPerMTok: 0.6, outputPerMTok: 12 }],
		source: OPENAI,
		verifiedOn: V,
		note: 'RogerATC’s default voice. Priced per token only — text in, audio out; OpenAI publishes no per-minute or per-character figure for it, so a line with only seconds or characters is unknown.',
	},
	{
		provider: 'openai',
		model: 'tts-1',
		kind: 'tts',
		periods: [{ perMCharacters: 15 }],
		source: OPENAI,
		verifiedOn: V,
	},
	{
		provider: 'openai',
		model: 'tts-1-hd',
		kind: 'tts',
		periods: [{ perMCharacters: 30 }],
		source: OPENAI,
		verifiedOn: V,
	},

	// --- Anthropic: the EFB assistant's catalogue ----------------------------
	llm('anthropic', 'claude-opus-5', ANTHROPIC, {
		inputPerMTok: 5,
		cachedInputPerMTok: 0.5,
		outputPerMTok: 25,
	}),
	llm(
		'anthropic',
		'claude-sonnet-5',
		ANTHROPIC,
		{ inputPerMTok: 2, cachedInputPerMTok: 0.2, outputPerMTok: 10 },
		'The $2/$10 launch price was made the standard price; the announced rise to $3/$15 did not happen.',
	),
	llm('anthropic', 'claude-haiku-4-5', ANTHROPIC, {
		inputPerMTok: 1,
		cachedInputPerMTok: 0.1,
		outputPerMTok: 5,
	}),
	// The current generation, 2026-10-12. Cache hits are 5 % of input on Opus
	// and Sonnet 5.5 and 2.5 % on Fable 5.1, not the usual 10 %.
	llm(
		'anthropic',
		'claude-fable-5-1',
		ANTHROPIC,
		{ inputPerMTok: 10, cachedInputPerMTok: 0.25, outputPerMTok: 50 },
		undefined,
		V12,
	),
	llm(
		'anthropic',
		'claude-opus-5-5',
		ANTHROPIC,
		{ inputPerMTok: 4, cachedInputPerMTok: 0.2, outputPerMTok: 20 },
		undefined,
		V12,
	),
	llm(
		'anthropic',
		'claude-sonnet-5-5',
		ANTHROPIC,
		{ inputPerMTok: 2, cachedInputPerMTok: 0.1, outputPerMTok: 10 },
		undefined,
		V12,
	),
	llm(
		'anthropic',
		'claude-haiku-5-5',
		ANTHROPIC,
		{ inputPerMTok: 0.1, cachedInputPerMTok: 0.01, outputPerMTok: 0.5 },
		'Prompts up to 100,000 tokens; above that every token of the request costs five times as much, which a summed figure cannot see.',
		V12,
	),
	llm(
		'anthropic',
		'claude-opus-4-8',
		ANTHROPIC,
		{ inputPerMTok: 5, cachedInputPerMTok: 0.5, outputPerMTok: 25 },
		undefined,
		V12,
	),
	llm(
		'anthropic',
		'claude-sonnet-4-6',
		ANTHROPIC,
		{ inputPerMTok: 3, cachedInputPerMTok: 0.3, outputPerMTok: 15 },
		undefined,
		V12,
	),

	// --- xAI: the EFB assistant's catalogue ----------------------------------
	llm(
		'xai',
		'grok-4.7',
		XAI,
		{ inputPerMTok: 2, cachedInputPerMTok: 0.5, outputPerMTok: 6 },
		'Prompts under 200K tokens.',
		V12,
	),
	llm(
		'xai',
		'grok-4.6',
		XAI,
		{ inputPerMTok: 2, cachedInputPerMTok: 0.5, outputPerMTok: 6 },
		'Prompts under 200K tokens.',
	),
	llm(
		'xai',
		'grok-4.5',
		XAI,
		{ inputPerMTok: 2, cachedInputPerMTok: 0.3, outputPerMTok: 6 },
		'Prompts under 200K tokens.',
	),
	llm(
		'xai',
		'grok-4.3',
		XAI,
		{ inputPerMTok: 1.25, cachedInputPerMTok: 0.2, outputPerMTok: 2.5 },
		'Prompts under 200K tokens.',
	),

	// --- Google, reached only through OpenRouter -----------------------------
	{
		provider: 'google',
		model: 'gemini-3.8-flash',
		kind: 'llm',
		periods: [
			{ to: '2026-12-31', inputPerMTok: 0.75, cachedInputPerMTok: 0.075, outputPerMTok: 3.75 },
			{ from: '2027-01-01', inputPerMTok: 1.5, cachedInputPerMTok: 0.15, outputPerMTok: 7.5 },
		],
		source: GOOGLE,
		verifiedOn: V,
		note: 'Google publishes both prices: the lower one through 31 December 2026.',
	},

	// --- Tavily: RogerATC's instructor's search ------------------------------
	{
		provider: 'tavily',
		model: 'basic',
		kind: 'search',
		periods: [{ perRequest: 0.008 }],
		source: TAVILY,
		verifiedOn: V,
		note: 'One API credit per basic search, $0.008 per credit pay-as-you-go. A monthly plan’s free credits are not visible to us.',
	},
	{
		provider: 'tavily',
		model: 'advanced',
		kind: 'search',
		periods: [{ perRequest: 0.016 }],
		source: TAVILY,
		verifiedOn: V,
		note: 'Two API credits per advanced search.',
	},
])

/** Providers that run on the pilot's own machine: free whatever the model. */
export const LOCAL_PROVIDERS: readonly string[] = ['ollama']

/**
 * OpenRouter's vendor prefixes, mapped to the provider whose prices it passes
 * through (`anthropic/claude-opus-5` → anthropic's `claude-opus-5`).
 * Source: https://openrouter.ai/docs/faq, read 2026-10-09 — "We pass through
 * the pricing of the underlying providers; there is no markup on inference
 * pricing."
 */
const OPENROUTER_VENDORS: Record<string, string> = {
	anthropic: 'anthropic',
	openai: 'openai',
	'x-ai': 'xai',
	google: 'google',
}

/** `gpt-4o-mini-2024-07-18`, `claude-haiku-4-5-20251001`: a dated snapshot of an id. */
function isSnapshotOf(model: string, id: string): boolean {
	if (model === id) return true
	if (!model.startsWith(`${id}-`)) return false
	return /^(\d{4}-\d{2}-\d{2}|\d{8})$/.test(model.slice(id.length + 1))
}

function entryFor(provider: string, model: string, kind: AiUsageKind): PriceEntry | null {
	return (
		PRICE_TABLE.find(
			(entry) => entry.provider === provider && entry.kind === kind && isSnapshotOf(model, entry.model),
		) ?? null
	)
}

/**
 * The price entry that applies to a line: the provider's own, or — through
 * OpenRouter — the vendor's it passes through. Null when nobody published one
 * that we have verified.
 */
export function priceEntryFor(line: Pick<AiUsageLine, 'provider' | 'model' | 'kind'>): PriceEntry | null {
	if (line.provider === 'openrouter') {
		const slash = line.model.indexOf('/')
		if (slash <= 0) return null
		const vendor = OPENROUTER_VENDORS[line.model.slice(0, slash)]
		// `:free`, `:nitro` and friends are OpenRouter's own variants at their own
		// prices; only the plain id is the vendor's.
		let model = line.model.slice(slash + 1)
		if (!vendor || model.includes(':')) return null
		// OpenRouter writes Anthropic's versions with a dot (`claude-sonnet-5.5`)
		// where Anthropic's own id has a dash (`claude-sonnet-5-5`), read from
		// both catalogues on 2026-10-12. Without this every current Claude model
		// through OpenRouter would read as unknown.
		if (vendor === 'anthropic') model = model.replace(/\./g, '-')
		return entryFor(vendor, model, line.kind)
	}
	return entryFor(line.provider, line.model, line.kind)
}

/** The rates in force on `day` (`YYYY-MM-DD`), or on the day the table was verified. */
export function ratesOn(entry: PriceEntry, day: string = PRICES_VERIFIED_ON): PriceRates | null {
	return (
		entry.periods.find(
			(period) => (!period.from || period.from <= day) && (!period.to || day <= period.to),
		) ?? null
	)
}

const perMillion = (count: number, rate: number) => (count * rate) / 1_000_000

/**
 * Estimated USD for one line on one day, or `null` when it cannot be
 * estimated honestly: a model with no verified price, or a quantity the line
 * reports that its price is not expressed in, or requests with nothing metered
 * at all (a provider that reported no usage). Local providers cost 0.
 */
export function estimateCost(line: AiUsageLine, day?: string): number | null {
	if (LOCAL_PROVIDERS.includes(line.provider)) return 0
	const entry = priceEntryFor(line)
	if (!entry) return null
	const rates = ratesOn(entry, day)
	if (!rates) return null

	const tokens = line.inputTokens + line.outputTokens
	let cost = 0
	let priced = false

	switch (line.kind) {
		case 'llm': {
			if (rates.inputPerMTok === undefined || rates.outputPerMTok === undefined) return null
			if (line.requests > 0 && tokens === 0) return null
			const cached = Math.min(line.cachedInputTokens, line.inputTokens)
			cost += perMillion(line.inputTokens - cached, rates.inputPerMTok)
			cost += perMillion(cached, rates.cachedInputPerMTok ?? rates.inputPerMTok)
			cost += perMillion(line.outputTokens, rates.outputPerMTok)
			return cost
		}
		case 'stt': {
			if (rates.perMinute === undefined) return null
			if (line.requests > 0 && line.audioSeconds === 0) return null
			return (line.audioSeconds / 60) * rates.perMinute
		}
		case 'tts': {
			// Priced by whichever unit the provider bills in, and only if the line
			// carries it. Tokens first: they are the provider's own count.
			if (tokens > 0 && rates.inputPerMTok !== undefined && rates.outputPerMTok !== undefined) {
				cost += perMillion(line.inputTokens, rates.inputPerMTok)
				cost += perMillion(line.outputTokens, rates.outputPerMTok)
				priced = true
			} else if (line.characters > 0 && rates.perMCharacters !== undefined) {
				cost += perMillion(line.characters, rates.perMCharacters)
				priced = true
			} else if (line.audioSeconds > 0 && rates.perMinute !== undefined) {
				cost += (line.audioSeconds / 60) * rates.perMinute
				priced = true
			}
			if (priced) return cost
			return line.requests === 0 && tokens === 0 && line.characters === 0 && line.audioSeconds === 0
				? 0
				: null
		}
		case 'search': {
			if (rates.perRequest === undefined) return null
			return line.requests * rates.perRequest
		}
	}
}
